import React, { useEffect, useState } from "react";
import { apiFetchJson } from "../api";
import { t, tf, useLang, setLang, LANGUAGES } from "../i18n";
import {
  ArmorLogoIcon,
  SendIcon,
  MessageIcon,
  PackageIcon,
  TargetIcon,
  LinkIcon,
  FileIcon,
  SparklesIcon,
  RefreshIcon,
  CheckIcon,
  ChevronRightIcon,
  KeyIcon,
  XIcon,
  Spinner
} from "./Icons";
import RedeemKeyForm from "./RedeemKeyForm";

// Экран «Подписка не активна». Раньше это была одинокая карточка поверх
// тёмного фона, всегда тёмная и без выхода, кроме Telegram. Теперь это та же
// панель: её тема, её шрифт, её плитки, — и из него можно зайти в демо и
// потрогать разделы на вымышленных данных, пока подписки нет.
//
// Оформлено классами sub-gate-* в index.css: так экран сам переключается со
// светлой темой и графитом, как и остальные плитки панели.

// Разделы, которые можно открыть в демо прямо отсюда. Порядок — как в меню.
const buildFeatures = () => [
  { tab: "accounts", icon: LinkIcon, title: t("Аккаунты и прокси"), text: t("Статусы, прогрев, телефоны и проверка прокси") },
  { tab: "messages", icon: MessageIcon, title: t("Сообщения"), text: t("Все диалоги в одном окне, подсказки ответа и перевод") },
  { tab: "active-ads", icon: PackageIcon, title: t("Объявления"), text: t("Публикация, резерв, правка и история") },
  { tab: "sender", icon: SendIcon, title: "Sender", text: t("Рассылка по ссылкам и сбор email-лидов") },
  { tab: "kl-parser", icon: TargetIcon, title: t("Парсер"), text: t("Свежие объявления и скачки просмотров") },
  { tab: "drafts", icon: FileIcon, title: t("Черновики"), text: t("Отложенная публикация по расписанию") }
];

const DAY_FORMS_RU = ["день", "дня", "дней"];
const daysLabel = (lang, days) => {
  const value = Math.max(0, Math.round(Number(days) || 0));
  if (lang === "en") return `${value} ${value === 1 ? "day" : "days"}`;
  const mod10 = value % 10;
  const mod100 = value % 100;
  const form = mod10 === 1 && mod100 !== 11
    ? DAY_FORMS_RU[0]
    : (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? DAY_FORMS_RU[1] : DAY_FORMS_RU[2]);
  return `${value} ${form}`;
};

const formatPerDay = (lang, price, days) => {
  const perDay = Number(price) / Math.max(1, Number(days) || 1);
  if (!Number.isFinite(perDay)) return "";
  return perDay.toLocaleString(lang === "en" ? "en-GB" : "ru-RU", { maximumFractionDigits: perDay < 10 ? 1 : 0 });
};

// Миниатюра панели справа от заголовка: не картинка, а та же разметка в
// малом масштабе — плитки, диалоги, график. Нажатие открывает демо.
const PanelPreview = ({ onOpen }) => (
  <button type="button" className="kl-plain-button sub-gate-preview" onClick={onOpen} aria-label={t("Открыть демо-панель")}>
    <span className="sub-gate-preview-chrome">
      <span /><span /><span />
      <em>umbrella · demo</em>
    </span>
    <span className="sub-gate-preview-body">
      <span className="sub-gate-preview-rail">
        {[0, 1, 2, 3, 4, 5].map((index) => <i key={index} className={index === 0 ? "is-active" : ""} />)}
      </span>
      <span className="sub-gate-preview-main">
        <span className="sub-gate-preview-stats">
          <span><b>4</b><small>{t("аккаунта")}</small></span>
          <span><b>15</b><small>{t("объявлений")}</small></span>
          <span><b className="is-accent">3</b><small>{t("новых")}</small></span>
        </span>
        <span className="sub-gate-preview-chart">
          {[34, 48, 40, 62, 55, 78, 70, 92].map((height, index) => (
            <i key={index} style={{ height: `${height}%`, animationDelay: `${index * 70}ms` }} />
          ))}
        </span>
        <span className="sub-gate-preview-list">
          {[
            ["T", "Tobias K.", "Wäre 360 € inkl. Versand ok?", true],
            ["S", "Sabine", "Kann ich es heute Abend abholen?", true],
            ["A", "Anna-Lena", "Morgen 18 Uhr passt.", false]
          ].map(([letter, name, text, unread]) => (
            <span key={name} className="sub-gate-preview-row">
              <i>{letter}</i>
              <span>
                <b>{name}</b>
                <small>{text}</small>
              </span>
              {unread && <em />}
            </span>
          ))}
        </span>
      </span>
    </span>
    <span className="sub-gate-preview-cta">
      <SparklesIcon size={15} />
      {t("Открыть демо-панель")}
      <ChevronRightIcon size={15} />
    </span>
  </button>
);

const SubscriptionGate = ({
  username = "",
  avatarUrl = "",
  ownerId = "",
  isPhoneView = false,
  appVersion = "",
  telegramUrl = "https://t.me/umbrellapushbot",
  onStartDemo,
  onRecheck,
  onLogout,
  // Оплата прямо здесь — окном PaymentModal. Без него — по-старому, в бота.
  onBuy
}) => {
  const lang = useLang();
  const features = buildFeatures();
  const [plans, setPlans] = useState([]);
  const [recheck, setRecheck] = useState({ status: "idle", message: "" });
  const [copied, setCopied] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // Ключ подписки: строкой под «Проверить статус», по нажатию — поле.
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyDone, setKeyDone] = useState(null);

  // Тарифы — те же, что показывает бот: сервер отдаёт их без входа. Не
  // пришли — блок просто не рисуется, оплата всё равно идёт через Telegram.
  useEffect(() => {
    let alive = true;
    apiFetchJson("/api/plans", { timeoutMs: 15000, retry: false })
      .then((data) => {
        if (!alive) return;
        const list = (Array.isArray(data?.items) ? data.items : [])
          .filter((plan) => Number(plan?.days) > 0 && Number(plan?.priceUsd) > 0)
          .sort((a, b) => Number(a.days) - Number(b.days));
        setPlans(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const bestValueId = plans.length > 1
    ? plans.reduce((best, plan) => (Number(plan.priceUsd) / Number(plan.days) < Number(best.priceUsd) / Number(best.days) ? plan : best), plans[0]).id
    : "";

  const openTelegram = () => window.open(telegramUrl, "_blank", "noopener,noreferrer");
  const buy = (planId = "") => (onBuy ? onBuy(planId) : openTelegram());

  const startDemo = (tab = "dashboard") => {
    if (leaving) return;
    setLeaving(true);
    // Короткий уход экрана — и панель появляется уже на месте.
    window.setTimeout(() => onStartDemo?.(tab), 220);
  };

  const runRecheck = async () => {
    if (recheck.status === "loading") return;
    setRecheck({ status: "loading", message: "" });
    try {
      const result = await onRecheck?.();
      if (result?.active) return;
      setRecheck({ status: "idle", message: t("Подписка пока не активна. Если вы только что оплатили, подождите минуту и проверьте снова.") });
    } catch (_) {
      setRecheck({ status: "idle", message: t("Не удалось проверить статус. Повторите позже.") });
    }
  };

  // Ключ принят — дни уже на подписке; проверка статуса открывает панель.
  const handleKeyRedeemed = async (response) => {
    setKeyDone({ days: Number(response?.days) || 0 });
    setKeyOpen(false);
    try {
      await onRecheck?.();
    } catch (_) {
      // Статус не перечитался — подскажет «Проверить статус».
    }
  };

  const copyOwnerId = async () => {
    if (!ownerId) return;
    try {
      await navigator.clipboard.writeText(ownerId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      // Буфер обмена недоступен — ID всё равно виден на экране.
    }
  };

  const initial = String(username || "?").trim().charAt(0).toUpperCase() || "?";

  return (
    <div className={`sub-gate${isPhoneView ? " is-phone" : ""}${leaving ? " is-leaving" : ""}`}>
      <div className="sub-gate-glow" aria-hidden="true" />
      <div className="sub-gate-inner">
        <header className="sub-gate-top">
          <div className="sub-gate-brand">
            <ArmorLogoIcon size={26} />
            <span>umbrella</span>
          </div>
          <div className="sub-gate-top-actions">
            <div className="sub-gate-lang" role="group" aria-label={t("Язык")}>
              {LANGUAGES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`kl-plain-button${lang === option.value ? " is-active" : ""}`}
                  onClick={() => setLang(option.value)}
                  aria-pressed={lang === option.value}
                  title={option.label}
                >
                  {option.value.toUpperCase()}
                </button>
              ))}
            </div>
            {!isPhoneView && (
              <div className="sub-gate-user" title={username}>
                <i>{avatarUrl ? <img src={avatarUrl} alt="" draggable={false} /> : initial}</i>
                <span>{username || "—"}</span>
              </div>
            )}
            <button type="button" className="kl-plain-button sub-gate-ghost" onClick={onLogout}>
              {t("Выйти")}
            </button>
          </div>
        </header>

        <section className="sub-gate-hero">
          <div className="sub-gate-copy">
            <div className="sub-gate-pill">
              <span className="sub-gate-pill-dot" />
              {t("Подписка не активна")}
            </div>
            <h1 className="sub-gate-title">
              {t("Панель на паузе.")}
              <br />
              <span>{t("Демо — открыто.")}</span>
            </h1>
            <p className="sub-gate-lead">
              {onBuy
                ? t("Оформите подписку прямо здесь — доступ откроется сразу после оплаты. А пока загляните в демо: все разделы работают на вымышленных данных, ничего не уходит на Kleinanzeigen и не тратит нейросеть.")
                : t("Оформите подписку в Telegram — доступ откроется сразу после оплаты. А пока загляните в демо: все разделы работают на вымышленных данных, ничего не уходит на Kleinanzeigen и не тратит нейросеть.")}
            </p>
            <div className="sub-gate-actions">
              <button type="button" className="kl-plain-button kl-press-fwd sub-gate-primary" onClick={() => buy()}>
                <SendIcon size={16} />
                {t("Оформить подписку")}
              </button>
              <button type="button" className="kl-plain-button kl-press-fwd sub-gate-secondary" onClick={() => startDemo("dashboard")}>
                <SparklesIcon size={16} />
                {t("Открыть демо")}
              </button>
            </div>
            <div className="sub-gate-recheck">
              <button type="button" className="kl-plain-button" onClick={runRecheck} disabled={recheck.status === "loading"}>
                {recheck.status === "loading" ? <Spinner size={13} /> : <RefreshIcon size={13} />}
                {t("Уже оплатили? Проверить статус")}
              </button>
              {recheck.message && <p>{recheck.message}</p>}
            </div>
            <div className="sub-gate-key">
              {keyDone ? (
                <p className="sub-gate-key-done">
                  <CheckIcon size={15} />
                  {tf`Ключ активирован: +${daysLabel(lang, keyDone.days)}. Открываем панель…`}
                </p>
              ) : keyOpen ? (
                <div className="sub-gate-key-card">
                  <div className="sub-gate-key-head">
                    <div>
                      <b>{t("Ключ подписки")}</b>
                      <span>{t("Ключ из розыгрыша или от администратора. Дни добавятся сразу, и панель откроется.")}</span>
                    </div>
                    <button
                      type="button"
                      className="kl-plain-button sub-gate-key-close"
                      onClick={() => setKeyOpen(false)}
                      aria-label={t("Закрыть")}
                      title={t("Закрыть")}
                    >
                      <XIcon size={15} />
                    </button>
                  </div>
                  <RedeemKeyForm autoFocus onRedeemed={handleKeyRedeemed} />
                </div>
              ) : (
                <button type="button" className="kl-plain-button sub-gate-key-toggle" onClick={() => setKeyOpen(true)}>
                  <KeyIcon size={13} />
                  {t("Есть ключ подписки? Активировать")}
                </button>
              )}
            </div>
          </div>
          {!isPhoneView && <PanelPreview onOpen={() => startDemo("dashboard")} />}
        </section>

        {plans.length > 0 && (
          <section className="sub-gate-section">
            <div className="sub-gate-section-head">
              <h2>{t("Тарифы")}</h2>
              <span>{onBuy ? t("Оплата в USDT через CryptoBot или xRocket") : t("Оплата в Telegram-боте, в USDT")}</span>
            </div>
            <div className="sub-gate-plans">
              {plans.map((plan) => {
                const best = plan.id === bestValueId;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    className={`kl-plain-button sub-gate-plan${best ? " is-best" : ""}`}
                    onClick={() => buy(plan.id)}
                  >
                    {best && <span className="sub-gate-plan-badge">{t("Выгоднее всего")}</span>}
                    <span className="sub-gate-plan-title">{plan.title}</span>
                    <span className="sub-gate-plan-days">{daysLabel(lang, plan.days)}</span>
                    <span className="sub-gate-plan-price">
                      {Number(plan.priceUsd)}
                      <small>USDT</small>
                    </span>
                    <span className="sub-gate-plan-per-day">
                      {tf`≈ ${formatPerDay(lang, plan.priceUsd, plan.days)} USDT в день`}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section className="sub-gate-section">
          <div className="sub-gate-section-head">
            <h2>{t("Что внутри")}</h2>
            <span>{t("Любой раздел открывается в демо одним нажатием")}</span>
          </div>
          <div className="sub-gate-features">
            {features.map((feature, index) => (
              <button
                key={feature.tab}
                type="button"
                className="kl-plain-button sub-gate-feature"
                style={{ animationDelay: `${120 + index * 50}ms` }}
                onClick={() => startDemo(feature.tab)}
              >
                <span className="sub-gate-feature-icon"><feature.icon size={18} /></span>
                <span className="sub-gate-feature-text">
                  <b>{feature.title}</b>
                  <small>{feature.text}</small>
                </span>
                <ChevronRightIcon size={16} className="sub-gate-feature-arrow" />
              </button>
            ))}
          </div>
        </section>

        <footer className="sub-gate-footer">
          <span>{t("Логин:")} <b>{username || "—"}</b></span>
          <button type="button" className="kl-plain-button" onClick={copyOwnerId} title={t("Скопировать")}>
            ID: <b>{ownerId || "—"}</b>
            {copied && <CheckIcon size={13} />}
          </button>
          {appVersion && <span className="sub-gate-version">{appVersion}</span>}
        </footer>
      </div>
    </div>
  );
};

export default SubscriptionGate;
